import React from "react";
import {
  MdAccessTime,
  MdAutoAwesome,
  MdBarChart,
  MdCheckCircle,
  MdExpandLess,
  MdExpandMore,
  MdLightbulb,
  MdPeople,
  MdRefresh,
  MdTrendingUp,
} from "react-icons/md";
import Badge from "components/badge/Badge";
import Button from "components/button";
import Card, { CardHeader } from "components/card";
import { EmptyState } from "components/states";
import { formatDate } from "utils/format";

const URGENCY_TONES = { high: "danger", medium: "warning" };

const indicatorTone = (value) => {
  if (value === "strong" || value === "healthy") return "success";
  if (value === "needs_attention" || value === "low") return "danger";
  return "warning";
};

const plural = (count) => (count !== 1 ? "s" : "");

const Section = ({ icon: Icon, iconClassName, title, children }) => (
  <section className="rounded-control border border-line p-4">
    <h3 className="mb-3 flex items-center gap-2 font-semibold text-fg">
      <Icon aria-hidden="true" className={`h-5 w-5 ${iconClassName}`} />
      {title}
    </h3>
    {children}
  </section>
);

const BulletList = ({ items, dotClassName, format = (item) => item }) => (
  <ul className="space-y-2">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-2 text-fg">
        <span
          aria-hidden="true"
          className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${dotClassName}`}
        />
        {format(item)}
      </li>
    ))}
  </ul>
);

const ListOrText = ({ value, dotClassName, textClassName = "" }) =>
  Array.isArray(value) ? (
    <BulletList items={value} dotClassName={dotClassName} />
  ) : (
    <p className={`text-fg ${textClassName}`.trim()}>{value}</p>
  );

const hasItems = (value) => Boolean(value) && (Array.isArray(value) ? value.length > 0 : true);

// Summaries come in several shapes: guest summaries put content under `sections`,
// spiritual summaries under `summary`, and older payloads at the root.
const normalizeSummary = (aiSummary) => {
  const sections = aiSummary.sections || {};
  const summary = aiSummary.summary || {};
  return {
    executiveSummary:
      sections.executiveSummary || summary.executiveSummary || aiSummary.executiveSummary,
    keyInsights:
      sections.keyFindings ||
      sections.keyInsights ||
      summary.keyInsights ||
      aiSummary.keyInsights ||
      [],
    strengthAreas: summary.strengthAreas || aiSummary.strengthAreas || [],
    growthOpportunities: summary.growthOpportunities || aiSummary.growthOpportunities || [],
    pastoralPriorities: summary.pastoralPriorities || aiSummary.pastoralPriorities || [],
    trendIndicators: summary.trendIndicators || aiSummary.trendIndicators,
    recommendedActions: sections.recommendedActions || [],
    spiritualNeeds: sections.spiritualNeeds,
    followUpPriorities: sections.followUpPriorities,
  };
};

const SummaryContent = ({ aiSummary }) => {
  const {
    executiveSummary,
    keyInsights,
    strengthAreas,
    growthOpportunities,
    pastoralPriorities,
    trendIndicators,
    recommendedActions,
    spiritualNeeds,
    followUpPriorities,
  } = normalizeSummary(aiSummary);

  if (!executiveSummary && keyInsights.length === 0 && !aiSummary.rawText) return null;

  return (
    <div className="space-y-4">
      {executiveSummary && (
        <section className="rounded-control bg-surface-raised p-4">
          <h3 className="mb-2 flex items-center gap-2 font-semibold text-fg">
            <MdLightbulb aria-hidden="true" className="h-5 w-5 text-warning-fg" />
            Executive Summary
          </h3>
          <p className="text-fg">{executiveSummary}</p>
        </section>
      )}

      {keyInsights.length > 0 && (
        <Section icon={MdTrendingUp} iconClassName="text-success-fg" title="Key Insights">
          <BulletList items={keyInsights} dotClassName="bg-success" />
        </Section>
      )}

      {hasItems(spiritualNeeds) && (
        <Section icon={MdPeople} iconClassName="text-info-fg" title="Spiritual Needs">
          <ListOrText
            value={spiritualNeeds}
            dotClassName="bg-info"
            textClassName="whitespace-pre-line"
          />
        </Section>
      )}

      {recommendedActions.length > 0 && (
        <Section icon={MdTrendingUp} iconClassName="text-info-fg" title="Recommended Actions">
          <BulletList
            items={recommendedActions}
            dotClassName="bg-info"
            format={(action) => action.replace(/^\d*\.\s*/, "")}
          />
        </Section>
      )}

      {hasItems(followUpPriorities) && (
        <Section icon={MdAccessTime} iconClassName="text-warning-fg" title="Follow-up Priorities">
          <ListOrText value={followUpPriorities} dotClassName="bg-warning" />
        </Section>
      )}

      {strengthAreas.length > 0 && (
        <Section icon={MdCheckCircle} iconClassName="text-success-fg" title="Strengths">
          <BulletList items={strengthAreas} dotClassName="bg-success" />
        </Section>
      )}

      {growthOpportunities.length > 0 && (
        <Section icon={MdTrendingUp} iconClassName="text-info-fg" title="Growth Opportunities">
          <BulletList items={growthOpportunities} dotClassName="bg-info" />
        </Section>
      )}

      {pastoralPriorities.length > 0 && (
        <Section icon={MdAccessTime} iconClassName="text-warning-fg" title="Pastoral Priorities">
          <ul className="space-y-3">
            {pastoralPriorities.map((item, idx) => (
              <li key={idx} className="rounded-control bg-surface-raised p-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="font-medium text-fg">{item.priority}</span>
                  <Badge tone={URGENCY_TONES[item.urgency] || "neutral"} className="capitalize">
                    {item.urgency} priority
                  </Badge>
                </div>
                <p className="text-sm text-fg-muted">{item.suggestedAction}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {trendIndicators && (
        <Section icon={MdBarChart} iconClassName="text-primary dark:text-accent" title="Health Indicators">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Object.entries(trendIndicators).map(([key, value]) => (
              <div key={key} className="flex flex-col-reverse items-center gap-1 text-center">
                <dt className="text-xs capitalize text-fg-muted">
                  {key.replace(/([A-Z])/g, " $1").trim()}
                </dt>
                <dd>
                  <Badge tone={indicatorTone(value)} className="capitalize">
                    {String(value).replace(/_/g, " ")}
                  </Badge>
                </dd>
              </div>
            ))}
          </dl>
        </Section>
      )}
    </div>
  );
};

const SurveyAnalysisCard = ({
  aiSummary,
  responseCount,
  generating,
  onGenerate,
  expanded,
  onToggleExpanded,
}) => {
  const renderBody = () => {
    if (responseCount === 0) {
      return (
        <EmptyState
          icon={MdPeople}
          title="No Responses Yet"
          description="Survey analysis will be available once you have responses."
        />
      );
    }
    if (!aiSummary) {
      return (
        <EmptyState
          icon={MdAutoAwesome}
          title="No Analysis Yet"
          description={`Select "Generate Analysis" to get insights from your ${responseCount} survey response${plural(responseCount)}.`}
        />
      );
    }
    const basedOn = aiSummary.responseCount || responseCount;
    return (
      <div className="mt-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-fg-muted">
          <span>
            Based on {basedOn} response{plural(basedOn)}
          </span>
          <span>
            Generated {aiSummary.generatedAt ? formatDate(aiSummary.generatedAt) : "recently"}
          </span>
        </div>

        {expanded && (
          <div id="survey-analysis-content" className="space-y-4">
            <SummaryContent aiSummary={aiSummary} />
            {aiSummary.source && (
              <p className="flex items-center gap-2 text-xs text-fg-muted">
                <MdAutoAwesome aria-hidden="true" className="h-3 w-3" />
                Auto-generated
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card padding="md">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <MdAutoAwesome aria-hidden="true" className="h-5 w-5 text-primary dark:text-accent" />
            Survey Analysis
          </span>
        }
        actions={
          <>
            {aiSummary && (
              <Button
                variant="ghost"
                size="sm"
                aria-expanded={expanded}
                aria-controls="survey-analysis-content"
                rightIcon={
                  expanded ? (
                    <MdExpandLess aria-hidden="true" className="h-4 w-4" />
                  ) : (
                    <MdExpandMore aria-hidden="true" className="h-4 w-4" />
                  )
                }
                onClick={onToggleExpanded}
              >
                {expanded ? "Hide Details" : "Show Details"}
              </Button>
            )}
            <Button
              variant={aiSummary ? "secondary" : "primary"}
              size="sm"
              loading={generating}
              disabled={responseCount === 0}
              leftIcon={
                aiSummary ? <MdRefresh aria-hidden="true" /> : <MdAutoAwesome aria-hidden="true" />
              }
              onClick={onGenerate}
            >
              {generating ? "Analyzing…" : aiSummary ? "Regenerate" : "Generate Analysis"}
            </Button>
          </>
        }
      />
      {renderBody()}
    </Card>
  );
};

export default SurveyAnalysisCard;
